import React from 'react';
import immutable from 'immutable';
import ReactSelectize from 'react-selectize';
import ConstValues from 'utils/const';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import 'styles/call-card/sync-crm/CallSyncCRM.styl';

const classNames = require('classnames');
const LoadingGif = require('images/card/Loading.gif');

/**
 * CallSyncCRMRelatedToSearchInputBox is a component that allows any user / manager to search salesforce accounts /
 * opportunities within the user's salesforce account
 */
export default class CallSyncCRMRelatedToSearchInputBox extends React.Component {
  constructor() {
    super();
    this.state = {
      // options store the current list of selectable options for the related-to field. Notice that the list of options
      // depend on weather the contact-and-lead field has been selected or not and if it's selected, which type of value
      // is chosen for the contact-and-lead field
      options: [],
      // previous search term
      previousSearch: '',
      // current search term
      search: '',
      // values store the current array of selected options for the related-to field. If selected, then this array
      // should always be a one-element array
      values: [],
      open: false,
      // queuedSearch stores a chosen search term that's queued due to rate-limit on the backend
      queuedSearch: null,
      // runningSearch stores a chosen search term that's queued due to rate-limit on the backend
      runningSearch: null,
      // isFocusedControl is true when the Related-To search input box is the current focus
      isFocusedControl: false,
    };
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onValuesChange = this.onValuesChange.bind(this);
    this.renderNoResultsFound = this.renderNoResultsFound.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onOpenChange = this.onOpenChange.bind(this);
  }

  /**
   * Return the type of value chosen for contacts-and-leads in a given props
   * @param {object} props Props of this component
   * @returns {object / null}
   */
  contactOrLeadValueTypeFromProps(props) {
    const value = props.contactsAndLeadsValue;
    if (value && value.option) {
      return value.option.type;
    }
    return null;
  }

  /**
   * Populate this.state.values with an account if it's already chosen. This use case might happen if the user selected
   * a contact from the contact-and-lead-search-input-box
   * @param {Object} props New props passed in by the parent container component
   */
  populateCurrentValuesIfNecessary(props) {
    if (
      (props.accountAndOpportunitiesValue &&
        props.accountAndOpportunitiesValue.option.type === 'account') ||
      props.accountAndOpportunitiesValue !== immutable.List(this.state.values).first()
    ) {
      if (props.accountAndOpportunitiesValue) {
        this.setState({values: [props.accountAndOpportunitiesValue]});
      } else {
        this.setState({values: []});
      }
    }
  }

  /** Trigger CrmSyncActions.findAccountsAndOpportunitiesByContactName when the user selects a contact-account-id value
   *  @param {object} nextProps props passed in by the parent container component
   **/
  UNSAFE_componentWillReceiveProps(nextProps) {
    let valueType = this.contactOrLeadValueTypeFromProps(this.props);
    if (this.props.contactsAndLeadsValue !== nextProps.contactsAndLeadsValue) {
      valueType = this.fetchAccountAndOpportunitiesFromServerIfNecessary(nextProps, valueType);
    }
    this.populateCurrentValuesIfNecessary(this.props);
    this.populateOptionsWithAccountAndOpportunitiesIfNecessary(nextProps, valueType);
    this.populateOptionsWithAccountsAndOpportunitiesFromPrefixSearchIfNecessary(
      nextProps,
      valueType
    );
    this.runQueuedSearchIfNecessaryOrClearSearchState(nextProps);
  }

  /**
   * Run this.state.queuedSearch if necessary, e.g., if this.state.queuedSearch is not null and if nextProps.loading is
   * false. This function should also set queuedSearch and runningSearch back to null if this.state.queuedSearch is
   * equal to this.state.runningSearch
   * @param {Object} nextProps
   */
  runQueuedSearchIfNecessaryOrClearSearchState(nextProps) {
    if (this.props.prefixSearchLoading && !nextProps.prefixSearchLoading) {
      if (this.state.queuedSearch && this.state.queuedSearch !== this.state.runningSearch) {
        this.prefixSearch(this.state.queuedSearch);
      } else if (this.state.queuedSearch && this.state.queuedSearch === this.state.runningSearch) {
        this.setState({queuedSearch: null, runningSearch: null});
      } else {
        this.setState({runningSearch: null});
      }
    }
  }

  /**
   * Trigger a CrmSyncActions to ask the related-to store to update its account-and-opportunities using data
   * fetched from the backend only when it's necessary
   * @param {object} nextProps New props for this component
   * @param {string} valueType Type of the current chosen contact/lead value
   * @returns {string} valueType Type of the current contact/lead value updated according to nextProps
   */
  fetchAccountAndOpportunitiesFromServerIfNecessary(nextProps, valueType) {
    const value = nextProps.contactsAndLeadsValue;
    if (value && value.option) {
      // noinspection JSValidateTypes
      valueType = this.contactOrLeadValueTypeFromProps(nextProps);
      if (valueType === 'contact') {
        setImmediate(() => {
          CrmSyncActions.findAccountsAndOpportunitiesByContactName(value.option.name);
        });
      }
    }
    return valueType;
  }

  /**
   * Populate this.state.options with accountAndOpportunities only if it's necessary, e.g., only when the current
   * valueType is 'contact' and there are valid account-and-opportunities from either this.props or nextProps
   * @param {object} nextProps New props obtained from the parent container
   * @param {string} valueType Type of the currently selected value, i.e., 'contact' or 'opportunity' or null
   */
  populateOptionsWithAccountAndOpportunitiesIfNecessary(nextProps, valueType) {
    let accountAndOpportunities = this.props.accountAndOpportunities;
    if (nextProps.accountAndOpportunities) {
      // Populate this.state.options with nextProps
      accountAndOpportunities = nextProps.accountAndOpportunities;
    }
    if (accountAndOpportunities) {
      this.populateOptionsWithAccountAndOpportunities(accountAndOpportunities);
    }
  }

  /**
   * Return a list of account(s) filtered from the list of account-and-opportunities. We return a list with only
   * accounts when the user selects a contact from the contactAndLeads input box
   * @param {immutable.List} accountAndOpportunities List of account-and-opportunities
   */
  accountOnlyFrom(accountAndOpportunities) {
    return accountAndOpportunities.filter(val => val.type === 'account');
  }

  /**
   * Populate this.state.options with accountsAndOpportunitiesFromPrefixSearch only if it's necessary, e.g., only when
   * the current valueType is null and there are valid accounts-and-opportunities-from-prefix-search results from either
   * this.props or nextProps
   * @param {object} nextProps New props obtained from the parent container
   * @param {string} valueType Type of the currently selected value, i.e., 'contact' or 'opportunity' or null
   */
  populateOptionsWithAccountsAndOpportunitiesFromPrefixSearchIfNecessary(nextProps, valueType) {
    if (valueType === null) {
      let accountsAndOpportunities;
      if (
        nextProps.accountsAndOpportunitiesFromPrefixSearch !== this.props.accountsAndOpportunities
      ) {
        accountsAndOpportunities = nextProps.accountsAndOpportunitiesFromPrefixSearch;
      }
      if (accountsAndOpportunities) {
        const total = this.totalAccountsAndOpportunitiesFromPrefixSearch(nextProps);
        const numReturned = this.numReturnedAccountsAndOpportunitiesFromPrefixSearch(nextProps);
        this.populateOptionsWithAccountsAndOpportunitiesFromPrefixSearch(
          accountsAndOpportunities,
          total,
          numReturned
        );
      }
    }
  }

  /** Return the current total accounts-and-opportunities from prefix search */
  totalAccountsAndOpportunitiesFromPrefixSearch(nextProps) {
    let total = this.props.accountsAndOpportunitiesNumReturned;
    if (nextProps.accountsAndOpportunitiesNumReturned !== total) {
      total = nextProps.accountsAndOpportunitiesNumReturned;
    }
    return total;
  }

  /** Return the current number of returned accounts-and-opportunities from prefix search */
  numReturnedAccountsAndOpportunitiesFromPrefixSearch(nextProps) {
    let numReturned = this.props.accountsAndOpportunitiesNumReturned;
    if (nextProps.accountsAndOpportunitiesNumReturned !== numReturned) {
      numReturned = nextProps.accountsAndOpportunitiesNumReturned;
    }
    return numReturned;
  }

  /**
   * Populate this.state.options with accounts-and-opportunities found from a prefix search
   * @param {immutable.List} accountsAndOpportunities
   * @param {number} total
   * @param {number} numReturned
   */
  populateOptionsWithAccountsAndOpportunitiesFromPrefixSearch(
    accountsAndOpportunities,
    total,
    numReturned
  ) {
    let options = accountsAndOpportunities.map(value => {
      return {
        value: value.name,
        label: value.name,
        id: value.id,
        option: value,
      };
    });
    const numRemaining = total - numReturned;
    if (numRemaining > 0 && options && options.count() > 0) {
      const indicatorOption = {
        value: numReturned,
        label: `${numRemaining}`,
        option: {
          type: ConstValues.remainingIndicatorOptionType,
          value: numRemaining,
        },
      };
      options = options.push(indicatorOption);
    }
    this.setState({options: options.toArray()});
  }

  /**
   * Populate this.state.options with account-and-opportunities since the user has chosen a contact-account in the
   * contact/leads field
   * @param {immutable.List} accountAndOpportunities account-and-opportunities for the related-to search input box
   */
  populateOptionsWithAccountAndOpportunities(accountAndOpportunities) {
    const options = accountAndOpportunities
      .map(value => {
        return {
          value: value.name,
          label: value.name,
          id: value.id,
          option: value,
        };
      })
      .toArray();
    this.setState({options: options});
  }

  /** Return true if <ReactSelectize.MultiSelect/> should be disabled or false if it should not be. It should be enabled
   *  when `this.props.contactsAndLeadsValue` is chosen with a contact-account value or when
   *  `this.props.contactsAndLeadsValue` is empty / not selected
   **/
  shouldDisableSelectize() {
    const value = this.props.contactsAndLeadsValue;
    if (value === undefined) {
      // No contact / lead value has been chosen; so we enable the related-to field
      return false;
    }
    if (value.option) {
      const valueType = value.option.type;
      if (valueType === 'contact') {
        // Enable the related-to field when the user selects a contact-account
        return false;
      } else if (valueType === 'lead') {
        // Disable the related-to field when the user selects a lead
        return true;
      }
    }
    // By default, disable the related-to field
    return true;
  }

  /**
   * Whenever the user changes the search term, we initiate a backend API request to fetch the accounts/opportunities
   * *OR* queue the search term in a temporary front-end storage and then initiate it when the current search request is
   * complete
   * @param term Current search term
   */
  onSearchChange(term) {
    let state = {search: term};
    if (term.length >= ConstValues.minimalNumberOfCharactersToTriggerSearch) {
      if (!this.props.prefixSearchLoading) {
        this.prefixSearch(term);
      } else {
        state.queuedSearch = term;
      }
    }
    if (term.length > 0) {
      // Sometimes this.state.search could be cleared by an onBlur event so we only update this.state.previousSearch if
      // term is a valid string
      state.previousSearch = term;
    }
    this.setState(state);
  }

  /**
   * Prefix-search a term to obtain accounts and opportunities
   * @param {string} term Search term
   */
  prefixSearch(term) {
    this.setState({runningSearch: term});
    setImmediate(() => {
      CrmSyncActions.prefixSearchAccountsAndOpportunities(term);
    });
  }

  /**
   * Whenever the user selects a value, we replace the current value if it's already selected or populate
   * `this.state.values` with the new item's value
   * @param {Array.<object>} items Array of selectize items that includes a account / opportunity option
   */
  onValuesChange(items) {
    let values;
    const lastItem = items[items.length - 1];
    if (lastItem && lastItem.option) {
      values = [lastItem];
    } else {
      values = [];
    }
    CrmSyncActions.updateAccountsAndOpportunitiesValues(values);
    this.updateContactsAndLeadsByRelatedTo(values);
    this.setState({values: values});
    if (values.length > 0) {
      this._select.blur();
    }
  }

  /**
   * Trigger a request to search for contacts from the server-side with a given / chosen account or opportunity
   * @param {Array.<string>} values Array of the currently selected values that contain one element
   */
  updateContactsAndLeadsByRelatedTo(values) {
    const valuesList = immutable.List(values);
    if (valuesList.count() > 0) {
      const accountId = this.accountIdFromAccountOrOpportunity(valuesList.first());
      if (accountId) {
        CrmSyncActions.findContactsByAccountId(accountId);
      }
    }
  }

  /**
   * Return the Account.Id value contained inside a account or opportunity
   * @param {object} item Item that is either a account or an opportunity
   */
  accountIdFromAccountOrOpportunity(item) {
    const option = item.option;
    if (option.type === 'account') {
      return option.id;
    } else if (option.type === 'opportunity') {
      return option.account.id;
    }
  }

  renderNoResultsFound(item, search) {
    let noResultFound = 'No results found';
    if (this.props.prefixSearchLoading || this.state.runningSearch || this.state.queuedSearch) {
      return (
        <div className="no-results-found">
          <span>Searching</span> <img alt="loading" src={LoadingGif} style={{height: '12px'}} />
        </div>
      );
    } else if (search.length < ConstValues.minimalNumberOfCharactersToTriggerSearch) {
      noResultFound = 'Start typing to find options';
    }
    return <div className="no-results-found">{noResultFound}</div>;
  }

  onBlur(event) {
    this.clearOptionsWhenNecessary();
    this.setState({
      isFocusedControl: false,
      runningSearch: null,
      queuedSearch: null,
    });
    CrmSyncActions.cancelPrefixSearchAccountsAndOpportunities();
  }

  onFocus(event) {
    this.clearOptionsWhenNecessary();
    this.setState({isFocusedControl: true});
  }

  clearOptionsWhenNecessary() {
    if (!(this.state.values.length > 0 || hasChosenContact(this.props))) {
      CrmSyncActions.clearAccountsAndOpportunitiesOptions();
    }
  }

  onOpenChange(open) {
    if (open && this.state.values.length > 0) {
      // Clear current value and show the search term with previous results
      CrmSyncActions.clearAccountsAndOpportunitiesValues();
      this.setState({
        previousSearch: '',
        search: this.state.previousSearch + this.state.search,
      });
    }
  }

  render() {
    const selectClassnames = classNames({
      'left-form-control': true,
      'long-form-control': true,
      'input-focused': this.state.isFocusedControl,
      'input-lost': !this.state.isFocusedControl,
    });
    const shouldDisableRelatedTo = this.shouldDisableSelectize();
    const placeholder = shouldDisableRelatedTo
      ? this.props.disabledPlaceholder
      : this.props.placeholder;
    return (
      <ReactSelectize.MultiSelect
        placeholder={placeholder}
        options={this.state.options}
        search={this.state.search}
        values={this.state.values}
        className={selectClassnames}
        renderOption={renderItemOption}
        renderValue={renderItemValue}
        onSearchChange={this.onSearchChange}
        disabled={shouldDisableRelatedTo}
        onValuesChange={this.onValuesChange}
        closeOnSelect={true}
        renderNoResultsFound={this.renderNoResultsFound}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        ref={select => (this._select = select)}
        onOpenChange={this.onOpenChange}
      />
    );
  }
}

/**
 * Custom item rendering function to render a one-line or multi-line selectize option based on `item`
 * @param {object} item Selectize item that includes a account / opportunity related option
 */
function renderItemOption(item) {
  const option = item.option;
  if (option.type === 'account') {
    return (
      <div className="crm-entry">
        <span className="account">
          <span className="name">{option.name}</span> <span className="type">{option.type}</span>
        </span>
      </div>
    );
  } else if (option.type === 'opportunity') {
    return (
      <div className="crm-entry">
        <span className="opportunity">
          <span className="name first-line">{option.name}</span>{' '}
          <span className="type first-line">{option.type}</span>
          <br />
          <span className="account">with {option.account.name}</span>
        </span>
      </div>
    );
  } else if (option.type === ConstValues.remainingIndicatorOptionType) {
    return (
      <div className="crm-entry unselectable">
        <span className="remaining-indicator">
          <span>{option.value} other results. Type more to narrow search</span>
        </span>
      </div>
    );
  }
}

/**
 * Custom item rendering function to render one-line selectize value using `item.option` based on its type
 * @param {object} item Selectize item that includes a account / opportunity related option
 */
function renderItemValue(item) {
  let itemValueSpan;
  const option = item.option;
  const outerSpanStyle = {
    verticalAlign: 'middle',
  };
  if (option.type === 'account') {
    itemValueSpan = (
      <span style={outerSpanStyle}>
        {option.name} <span className="capitalize">({option.type})</span>
      </span>
    );
  } else if (option.type === 'opportunity') {
    itemValueSpan = (
      <span style={outerSpanStyle}>
        {option.name} with {option.account.name} <span className="capitalize">({option.type})</span>
      </span>
    );
  }
  return <div className="simple-value">{itemValueSpan}</div>;
}

function hasChosenContact({contactsAndLeadsValue}) {
  return (
    contactsAndLeadsValue &&
    contactsAndLeadsValue.option.type === 'contact' &&
    contactsAndLeadsValue.id
  );
}
